import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';

import { AccountSmtpsService } from '../../core/services/account-smtps/account-smtps.service';
import { MailTemplatesService } from '../../core/services/mail-templates/mail-templates.service';
import { AlertService } from '../../core/services/alert/alert.service';
import { FormAddMailTemplateComponent } from './form-add-mail-template/form-add-mail-template.component';
import { FormAddSmtpComponent } from './form-add-smtp/form-add-smtp.component';

@Component({
  selector: 'app-mail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    FormAddMailTemplateComponent,
    FormAddSmtpComponent,
  ],
  templateUrl: './mail.component.html',
  styleUrl: './mail.component.css',
})
export class MailComponent implements OnInit {
  @ViewChild('templatePaginator') templatePaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // SMTP Mailboxes
  mailboxes: any[] = [];
  showSmtpForm = false;
  smtpData: any = null;

  // Logo
  logoUrl: string | null = null;
  logoConfigured = false;
  sections: { [key: string]: boolean } = { logo: true };

  // Templates table
  dataSource = new MatTableDataSource<any>();
  displayedColumns: string[] = ['name', 'subject', 'roles', 'updated_at', 'actions'];
  showTemplateForm = false;
  templateData: any = null;
  pageNumber = 1;
  pageSize = 25;

  constructor(
    private accountSmtpsService: AccountSmtpsService,
    private mailTemplatesService: MailTemplatesService,
    private alertService: AlertService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.alertService.loading('swal_messages.please_wait');
    this.loadMailboxes();
    this.loadTemplates();
  }

  // ============ SMTP MAILBOXES ============

  loadMailboxes(): void {
    this.accountSmtpsService.getSmtps().subscribe({
      next: (response) => {
        const data = response.data || [];
        // Default first, then alphabetical by label
        this.mailboxes = data.sort((a: any, b: any) => {
          if (a.is_default && !b.is_default) return -1;
          if (!a.is_default && b.is_default) return 1;
          return (a.label || '').localeCompare(b.label || '');
        });
      },
      error: () => {
        this.mailboxes = [];
      },
    });
  }

  getMailbox(reference: string): void {
    this.alertService.loading('swal_messages.please_wait');
    this.accountSmtpsService.getSmtp(reference).subscribe({
      next: (response) => {
        this.smtpData = response.data;
        this.showSmtpForm = true;
        this.alertService.close();
      },
      error: (error) => this.alertService.error(error.error),
    });
  }

  deleteMailbox(reference: string): void {
    this.alertService
      .confirmation('swal_messages.warning_confirm', 'settings.mail.mailboxes.confirm_delete', 'swal_messages.confirm')
      .then((result) => {
        if (result.isConfirmed) {
          this.alertService.loading('swal_messages.please_wait');
          this.accountSmtpsService.deleteSmtp(reference).subscribe({
            next: () => {
              this.loadMailboxes();
              this.alertService.successOk('swal_messages.account_smtps.account_smtp_deleted');
            },
            error: (error) => this.alertService.error(error.error),
          });
        }
      });
  }

  toggleSmtpForm(): void {
    this.smtpData = null;
    this.showSmtpForm = !this.showSmtpForm;
  }

  cerrarSmtpFormulario(): void {
    this.smtpData = null;
    this.showSmtpForm = false;
  }

  refrescarMailboxes(): void {
    this.showSmtpForm = false;
    this.smtpData = null;
    this.loadMailboxes();
  }

  // ============ LOGO ============

  toggleSection(section: string): void {
    this.sections[section] = !this.sections[section];
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (file.size > 4 * 1024 * 1024) {
      this.alertService.warning('swal_messages.file_too_large');
      return;
    }

    this.alertService.loading('swal_messages.please_wait');
    this.mailTemplatesService.uploadLogo(file).subscribe({
      next: (response) => {
        this.logoUrl = response.data.url;
        this.logoConfigured = true;
        this.sections['logo'] = false;
        this.alertService.successOk('swal_messages.mail_templates.mail_logo_uploaded');
      },
      error: (error) => this.alertService.error(error.error),
    });
  }

  // ============ TEMPLATES TABLE ============

  loadTemplates(): void {
    this.mailTemplatesService.getTemplates(this.pageNumber, this.pageSize).subscribe({
      next: (response) => {
        const templates = response.data || [];
        this.dataSource.data = templates;
        this.dataSource.sort = this.sort;
        this.alertService.close();

        if (!this.logoUrl) {
          const withLogo = templates.find((t: any) => t.logo_url);
          if (withLogo) {
            this.logoUrl = withLogo.logo_url;
            this.logoConfigured = true;
            this.sections['logo'] = false;
          }
        }
      },
      error: () => {
        this.alertService.close();
      },
    });
  }

  getTemplate(reference: string): void {
    this.alertService.loading('swal_messages.please_wait');
    this.mailTemplatesService.getTemplate(reference).subscribe({
      next: (response) => {
        this.templateData = response.data;
        this.showTemplateForm = true;
        this.alertService.close();
      },
      error: (error) => this.alertService.error(error.error),
    });
  }

  deleteTemplate(reference: string): void {
    this.alertService
      .confirmation('swal_messages.warning_confirm', 'swal_messages.mail_templates.deleting', 'swal_messages.confirm')
      .then((result) => {
        if (result.isConfirmed) {
          this.alertService.loading('swal_messages.please_wait');
          this.mailTemplatesService.deleteTemplate(reference).subscribe({
            next: () => {
              this.loadTemplates();
              this.alertService.successOk('swal_messages.mail_templates.mail_template_deleted');
            },
            error: (error) => this.alertService.error(error.error),
          });
        }
      });
  }

  toggleTemplateForm(): void {
    this.templateData = null;
    this.showTemplateForm = !this.showTemplateForm;
  }

  cerrarTemplateFormulario(): void {
    this.templateData = null;
    this.showTemplateForm = false;
  }

  refrescarTemplates(): void {
    this.showTemplateForm = false;
    this.loadTemplates();
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadTemplates();
  }

  getRolesDisplay(roles: string[]): string {
    if (!roles || roles.length === 0) return this.translate.instant('settings.mail.all_roles');
    return roles.join(', ');
  }
}
